import {
  ArrowLeftIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  LifebuoyIcon,
  LockSimpleIcon,
  PaperPlaneTiltIcon,
  SealCheckIcon,
  XIcon,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Skeleton,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";

import { useToast } from "@/client/hooks/use-toast";
import { USER_SPACE_PATHS } from "@/client/pages/client/user-space/config/navigation";
import { helpApiError, type HelpMessage } from "@/client/services/help/api";
import {
  CATEGORY_META,
  isHelpCategory,
  publicMessages,
  STATUS_META,
} from "@/client/services/help/display";
import {
  useCloseHelpRequest,
  useHelpRequest,
  useReopenHelpRequest,
  useSendHelpMessage,
} from "@/client/services/help/queries";

import { NewHelpRequestDialog } from "./new-request-dialog";

dayjs.extend(relativeTime);
dayjs.locale("fr");

/** Bulle d'un message du fil — alignement par rôle, style messagerie. */
const MessageBubble = ({ message }: { message: HelpMessage }) => {
  const isAdmin = message.authorRole === "admin";
  return (
    <div className={cn("flex w-full", isAdmin ? "justify-start" : "justify-end")}>
      <div
        className={cn(
          "max-w-[85%] space-y-1 sm:max-w-[75%]",
          isAdmin ? "items-start" : "items-end",
        )}
      >
        {isAdmin && (
          <p className="flex items-center gap-1 px-1 text-[11px] font-semibold text-emerald-700">
            <SealCheckIcon aria-hidden size={12} weight="fill" />
            Équipe MonCVpro
          </p>
        )}
        <div
          className={cn(
            "whitespace-pre-wrap break-words rounded-2xl px-4 py-2.5 text-[13px] leading-5 shadow-sm",
            isAdmin
              ? "rounded-bl-md border border-slate-200 bg-white text-slate-700"
              : "rounded-br-md bg-indigo-600 text-white",
          )}
        >
          {message.body}
        </div>
        <p className={cn("px-1 text-[11px] text-slate-400", isAdmin ? "text-left" : "text-right")}>
          {dayjs(message.createdAt).format("D MMMM YYYY à HH:mm")}
        </p>
      </div>
    </div>
  );
};

/**
 * Page détail d'une demande d'aide — fil de conversation type messagerie,
 * statut en header, clôture confirmée, composer avec Cmd+Entrée.
 */
export const UserSpaceHelpRequestDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data: request, isLoading, isError } = useHelpRequest(id ?? "");
  const sendMessage = useSendHelpMessage(id ?? "");
  const closeRequest = useCloseHelpRequest();
  const reopenRequest = useReopenHelpRequest();
  const { toast } = useToast();

  const [draft, setDraft] = useState("");
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const messages = request ? publicMessages(request.messages) : [];
  const isClosed = request?.status === "CLOSED";

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-[1240px] space-y-4 px-4 pb-24 pt-6 sm:px-6 lg:pb-6">
        <Skeleton className="h-5 w-40 rounded-lg" />
        <Skeleton className="h-28 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    );
  }

  if (isError || !request) {
    return (
      <div className="mx-auto w-full max-w-[1240px] px-4 pb-24 pt-6 sm:px-6 lg:pb-6">
        <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-200 bg-white/60 px-6 py-16 text-center">
          <span className="grid size-14 place-items-center rounded-2xl bg-slate-100 text-slate-400">
            <LifebuoyIcon aria-hidden size={28} weight="duotone" />
          </span>
          <h2 className="mt-4 text-[15px] font-semibold text-slate-900">Demande introuvable</h2>
          <p className="mt-1 text-[13px] text-slate-500">
            Elle a peut-être été supprimée ou le lien est incorrect.
          </p>
          <Button asChild variant="outline" className="mt-5 gap-2 rounded-xl">
            <Link to={USER_SPACE_PATHS.help}>
              <ArrowLeftIcon aria-hidden size={14} weight="bold" />
              Retour au centre d'aide
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const meta = isHelpCategory(request.category)
    ? CATEGORY_META[request.category]
    : CATEGORY_META.OTHER;
  const status = STATUS_META[request.status] ?? STATUS_META.OPEN;
  const Icon = meta.icon;

  const send = () => {
    const body = draft.trim();
    if (!body || sendMessage.isPending) return;
    sendMessage.mutate(
      { body, internalNote: false },
      {
        onSuccess: () => setDraft(""),
        onError: (error) =>
          toast({
            variant: "destructive",
            title: "Message non envoyé",
            description: helpApiError(error, "Réessayez dans un instant."),
          }),
      },
    );
  };

  const close = () => {
    closeRequest.mutate(request.id, {
      onSuccess: () =>
        toast({
          title: "Demande clôturée",
          description: "Merci — votre retour aide l'équipe à s'améliorer.",
        }),
      onError: (error) =>
        toast({
          variant: "destructive",
          title: "Clôture impossible",
          description: helpApiError(error, "Réessayez dans un instant."),
        }),
    });
  };

  return (
    <div className="mx-auto w-full max-w-[880px] px-4 pb-24 pt-6 sm:px-6 lg:pb-6">
      <Link
        to={USER_SPACE_PATHS.help}
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-500 transition-colors hover:text-indigo-600"
      >
        <ArrowLeftIcon aria-hidden size={14} weight="bold" />
        Centre d'aide
      </Link>

      {/* En-tête de la demande */}
      <div className="mt-3 flex flex-wrap items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5">
        <span className={cn("grid size-11 shrink-0 place-items-center rounded-xl", meta.tile)}>
          <Icon aria-hidden size={22} weight="duotone" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h1 className="text-lg font-bold tracking-tight text-slate-900">{request.subject}</h1>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
                status.badge,
              )}
            >
              {status.label}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">
            <span className="font-mono font-medium tracking-wide text-slate-400">
              {request.code}
            </span>
            <span className="mx-1.5">·</span>
            {meta.label}
            <span className="mx-1.5">·</span>
            ouverte {dayjs(request.createdAt).fromNow()}
          </p>
        </div>

        {!isClosed && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 rounded-xl text-slate-500 hover:text-red-600"
              >
                <XIcon aria-hidden size={14} weight="bold" />
                Clôturer
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="sm:rounded-2xl">
              <AlertDialogHeader>
                <AlertDialogTitle>Clôturer cette demande ?</AlertDialogTitle>
                <AlertDialogDescription>
                  La conversation sera définitivement fermée ({request.code}). Si le problème
                  réapparaît, vous pourrez ouvrir une nouvelle demande.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction
                  onClick={close}
                  className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-200"
                >
                  {closeRequest.isPending ? (
                    <CircleNotchIcon aria-hidden size={14} className="animate-spin" />
                  ) : (
                    <CheckCircleIcon aria-hidden size={14} weight="fill" />
                  )}
                  Clôturer la demande
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {/* Bandeau de statut résolu / close */}
      {(request.status === "RESOLVED" || isClosed) && (
        <div
          className={cn(
            "mt-3 flex items-center gap-2.5 rounded-xl p-3.5 text-[13px] ring-1",
            isClosed
              ? "bg-slate-50 text-slate-600 ring-slate-200"
              : "bg-emerald-50 text-emerald-800 ring-emerald-200",
          )}
        >
          {isClosed ? (
            <LockSimpleIcon aria-hidden size={16} weight="duotone" />
          ) : (
            <CheckCircleIcon aria-hidden size={16} weight="fill" className="text-emerald-600" />
          )}
          {isClosed ? (
            <span>
              Cette demande est close.{" "}
              <button
                type="button"
                className="font-semibold underline underline-offset-2"
                disabled={reopenRequest.isPending}
                onClick={() => {
                  reopenRequest.mutate(request.id, {
                    onSuccess: () =>
                      toast({
                        title: "Demande rouverte",
                        description: "Vous pouvez de nouveau écrire à l'équipe.",
                      }),
                    onError: (error) =>
                      toast({
                        variant: "destructive",
                        title: "Réouverture impossible",
                        description: helpApiError(error, "Réessayez plus tard."),
                      }),
                  });
                }}
              >
                Réouvrir cette demande
              </button>{" "}
              ou{" "}
              <button
                type="button"
                className="font-semibold underline underline-offset-2"
                onClick={() => setNewDialogOpen(true)}
              >
                ouvrir une nouvelle demande
              </button>{" "}
              pour un autre sujet.
            </span>
          ) : (
            <span>
              Le problème est marqué comme résolu — vous pouvez toujours ajouter un message si
              besoin.
            </span>
          )}
        </div>
      )}

      {/* Fil de conversation */}
      <div className="mt-4 space-y-4">
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      {isClosed ? (
        <p className="mt-6 text-center text-xs text-slate-400">
          Conversation close — réponses désactivées.
        </p>
      ) : (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                send();
              }
            }}
            rows={3}
            maxLength={4000}
            placeholder="Écrire un message à l'équipe…"
            className="shadow-xs w-full resize-y rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          />
          <div className="mt-3 flex items-center justify-between">
            <p className="text-[11px] text-slate-400">
              Réponse sous 24 h ouvrées · <kbd className="font-sans">Cmd</kbd>+
              <kbd className="font-sans">Entrée</kbd> pour envoyer
            </p>
            <Button
              onClick={send}
              disabled={sendMessage.isPending || draft.trim().length === 0}
              className="gap-2 rounded-xl"
            >
              {sendMessage.isPending ? (
                <CircleNotchIcon aria-hidden size={16} className="animate-spin" />
              ) : (
                <PaperPlaneTiltIcon aria-hidden size={16} weight="fill" />
              )}
              Envoyer
            </Button>
          </div>
        </div>
      )}

      <NewHelpRequestDialog
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
        openRequest={null}
      />
    </div>
  );
};
